import React, { useEffect, useRef, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import Alert from 'react-bootstrap/Alert'
import Helmet from 'react-helmet'
import { useSelector } from 'react-redux'
import { useParams, withRouter } from 'react-router-dom'

import Slide from '@material-ui/core/Slide'
import cogoToast from 'cogo-toast'
import DOMPurify from 'dompurify'
import Moment from 'moment'
import { useDebouncedCallback } from 'use-debounce'

import Activity from '../components/Activity'
import CameraScanModal from '../components/case/CameraScanModal'
import CaseNavigationBar from '../components/case/CaseNavigationBar'
import { HitlReviewModal } from '../components/case/HitlReview'
import SpeechReviewModal from '../components/case/SpeechReviewModal'
import SmartAddressInput from '../components/common/SmartAddressInput'
import AddButton from '../components/controls/AddButton'
import CheckBox from '../components/controls/CheckBox'
import DatePicker from '../components/controls/DatePicker'
import RemoveButton from '../components/controls/RemoveButton'
import TextArea from '../components/controls/TextArea'
import TimePicker from '../components/controls/TimePicker'
import CustomDropDown from '../components/CustomDropDown'
import Expense from '../components/Expense'
import FAIcon from '../components/FAIcon'
import NewCompanyModal from '../components/modals/NewCompanyModal'
import NavigationBar from '../components/NavigationBar'
import TranslationServiceSelecter from '../components/TranslationServiceSelecter'
import TransportationServiceSelecter from '../components/TransportationServiceSelecter'
import { buildStandardAppointmentActivities } from '../constants/ActivityTemplates'
import createFormHelpers from '../FormHelpers'
import sendRequest from '../services/api/sendRequest'
import AppointmentService from '../services/AppointmentsService'
import CasesService from '../services/CasesService'
import format from '../services/format'
import ProviderService from '../services/ProviderService'
import setFocusById from '../services/setFocusById'
import TasksService from '../services/TasksService'
import TranslationServicesService from '../services/TranslationServicesService'
import TransportationServicesService from '../services/TransportationServicesService'
import { buildDuplicatedAppointment, detectFollowUpSuggestion } from '../utils/appointmentDuplication'
import { SUGGESTED_FIELD_LENGTH } from '../utils/Constants'
import Permissions, { CAPABILITIES } from '../utils/Permissions'
import validation from '../validation'

import './NewAppointment.scss'

const NewAppointment = withRouter(({ history, location, isInline, refreshCaseStatus }) => {
  const currentUser = useSelector(state => state.currentUser)
  const { caseNumber, appointmentNumber } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [deleteForceWarning, setDeleteForceWarning] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const formRef = useRef(null)
  const isInitialMount = useRef(true)
  const createdAppointmentNumberRef = useRef(null)
  const [appointment, setAppointment] = useState({})
  const [appointmentDateAndTime, setAppointmentDateAndTime] = useState({
    date: null,
    startTime: '',
    endTime: '',
  })
  const [cases, setCases] = useState([])
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [treatmentProviders, setTreatmentProviders] = useState([])
  const [clientName, setClientName] = useState('')
  const [touched, setTouched] = useState({})
  const [newNote, setNewNote] = useState('')
  const [showRequiredActivitiesError, setShowRequiredActivitiesError] = useState(false)
  const [editingNoteIndex, setEditingNoteIndex] = useState(null)
  const [editingNoteText, setEditingNoteText] = useState('')

  // New Services Integration
  const [translationServices, setTranslationServices] = useState([])
  const [transportationServices, setTransportationServices] = useState([])
  const [showTranslationModal, setShowTranslationModal] = useState(false)
  const [showTransportationModal, setShowTransportationModal] = useState(false)
  const [serviceToEdit, setServiceToEdit] = useState(null)
  const [draggedIndex, setDraggedIndex] = useState(null)

  // Camera Scanning & Instant HITL Upload
  const [showCameraScanModal, setShowCameraScanModal] = useState(false)
  const [isAnalyzingSmartUpload, setIsAnalyzingSmartUpload] = useState(false)
  const [smartUploadDocuments, setSmartUploadDocuments] = useState([])
  const [showHitlModal, setShowHitlModal] = useState(false)
  const [hitlCaseModel, setHitlCaseModel] = useState(null)

  // Smart Speech Dictation & Multi-Entity HITL Extraction (#734)
  const [showSpeechModal, setShowSpeechModal] = useState(false)

  // Duplicate Appointment state (#568)
  const [duplicatedFromInfo, setDuplicatedFromInfo] = useState(null)

  const handleApplySpeechEntities = async payload => {
    setShowSpeechModal(false)
    if (!payload || !payload.entities) return

    const { entities, selectedCategories, appliedViaApi } = payload
    const activeCaseNum = caseNumber || appointment?.caseNumber

    const mappedNewTasks =
      selectedCategories?.tasks && entities.proposedTasks?.length > 0
        ? entities.proposedTasks
            .filter(t => t && t.title && String(t.title).trim())
            .map(t => {
              const rawDue = t.dueDateUtc || t.dueDate || Moment().add(3, 'days').format('YYYY-MM-DD')
              return {
                title: String(t.title).trim(),
                description: t.description || '',
                dueDateUtc: new Date(rawDue),
                isSaved: Boolean(appliedViaApi),
              }
            })
        : []

    if (appliedViaApi && appointmentNumber && activeCaseNum) {
      try {
        const refreshedAppt = await AppointmentService.get(activeCaseNum, appointmentNumber)
        if (refreshedAppt) {
          const refreshedActivities = (refreshedAppt.activities || []).map(act => ({
            ...act,
            clientActivityId:
              act.clientActivityId ||
              (act.activityNumber ? `act-${act.activityNumber}` : `new-${Math.random().toString(36).substr(2, 9)}`),
          }))
          setAppointment(prev => ({
            ...prev,
            ...refreshedAppt,
            activities: refreshedActivities,
            notes: refreshedAppt.notes || prev.notes || [],
            tasks: [...(prev.tasks || []), ...mappedNewTasks],
          }))
        }
      } catch (_err) {
        // Fallback to local state merge below if fetch fails
        setAppointment(prev => ({
          ...prev,
          tasks: [...(prev.tasks || []), ...mappedNewTasks],
        }))
      }
    } else {
      if (selectedCategories?.notes && entities.appointmentNotes) {
        setNewNote(prev => (prev ? `${prev}\n\n${entities.appointmentNotes}` : entities.appointmentNotes))
        setAppointment(prev => ({
          ...prev,
          note: prev.note ? `${prev.note}\n\n${entities.appointmentNotes}` : entities.appointmentNotes,
        }))
      }

      if (selectedCategories?.activities && entities.billableActivities?.length > 0) {
        const mappedNewActivities = entities.billableActivities.map(act => ({
          type: act.type || 'Coordination of Services',
          description: act.description || '',
          time: act.time || 0,
          date: act.date || Moment().format('YYYY-MM-DD'),
          activityNumber: 0,
          clientActivityId: `new-${Math.random().toString(36).substr(2, 9)}`,
        }))

        setAppointment(prev => ({
          ...prev,
          activities: [...(prev.activities || []), ...mappedNewActivities],
        }))
      }

      if (mappedNewTasks.length > 0) {
        if (activeCaseNum) {
          try {
            await Promise.all(
              mappedNewTasks.map(t =>
                TasksService.create(
                  {
                    caseNumber: parseInt(activeCaseNum, 10),
                    title: t.title,
                    dueDateUtc: t.dueDateUtc,
                    complete: false,
                    description: t.description || '',
                    note: '',
                    activities: [],
                    expenses: [],
                  },
                  activeCaseNum,
                ),
              ),
            )
            mappedNewTasks.forEach(t => {
              t.isSaved = true
            })
          } catch (_err) {
            // Keep isSaved = false so exit/save handler retries
          }
        }

        setAppointment(prev => ({
          ...prev,
          tasks: [...(prev.tasks || []), ...mappedNewTasks],
        }))
      }
    }

    if (refreshCaseStatus) {
      refreshCaseStatus()
    }

    cogoToast.success('Speech dictation applied to case and appointment successfully!', { position: 'top-right' })
  }

  const canScanOrUpdate =
    Permissions.can(currentUser, CAPABILITIES.UPDATE_CASE) || Permissions.can(currentUser, CAPABILITIES.CREATE_CASE)

  const handleCameraUploadFromAppointment = async payload => {
    setShowCameraScanModal(false)
    const filesToUpload =
      Array.isArray(payload?.files) && payload.files.length > 0 ? payload.files : payload?.file ? [payload.file] : []
    if (filesToUpload.length === 0) return

    const activeCaseNum = caseNumber || appointment.caseNumber
    if (!activeCaseNum) {
      cogoToast.error('Please select a client before scanning documents.', { position: 'top-right' })
      return
    }

    const file = filesToUpload[0]
    const docName = payload.name
    const category = payload.category
    const docDate = payload.documentDate
    const provId = payload.associatedProviderId || selectedProvider?.id || null
    const apptId = appointmentNumber
      ? parseInt(appointmentNumber, 10)
      : appointment?.appointmentNumber
      ? parseInt(appointment.appointmentNumber, 10)
      : appointment?.id || null

    const localDocs = filesToUpload.map((f, idx) => {
      const fExt = f.name && f.name.includes('.') ? f.name.split('.').pop().toLowerCase() : 'jpg'
      const pageBaseName = filesToUpload.length > 1 ? `${docName} - Page ${idx + 1}` : docName
      const normalizedFileName =
        pageBaseName && !pageBaseName.includes('.')
          ? `${pageBaseName}.${fExt}`
          : pageBaseName || f.name || `Scanned_Document_${idx + 1}.jpg`
      return {
        id: `doc-local-${Date.now()}-${idx}`,
        fileName: normalizedFileName,
        fileType: normalizedFileName.toLowerCase().endsWith('.pdf') ? 'pdf' : 'image',
        textContent: '',
        url: f.dataUrl || (idx === 0 ? payload.dataUrl : null) || URL.createObjectURL(f),
        blob: f,
      }
    })
    const localDoc = localDocs[0]

    setSmartUploadDocuments(localDocs)
    setIsAnalyzingSmartUpload(true)
    setShowHitlModal(true)

    try {
      const c = await CasesService.get(activeCaseNum)
      if (c) setHitlCaseModel(c)
    } catch (_err) {
      // Continue
    }

    try {
      const formData = new FormData()
      filesToUpload.forEach(f => {
        formData.append('files', f)
      })
      if (docName) formData.append('documentName', docName)
      if (category) formData.append('category', category)
      if (provId) formData.append('associatedProviderId', provId)
      if (payload.associatedProviderName || appointment.individualProvider || appointment.provider) {
        formData.append(
          'associatedProvider',
          payload.associatedProviderName || appointment.individualProvider || appointment.provider,
        )
      }
      if (docDate) formData.append('documentDate', docDate)
      if (apptId) formData.append('appointmentId', apptId)

      const startTime = Date.now()
      const response = await sendRequest({
        url: `/api/cases/${activeCaseNum}/smart-upload`,
        method: 'POST',
        data: formData,
        headers: { 'Content-Type': undefined },
      })

      const targetJobId = response?.jobId ? String(response.jobId).toLowerCase() : null
      let completedPending = response?.pendingExtractions || response?.extensions?.pendingExtractions
      let updatedCaseModel = response?.client ? response : null

      if (!completedPending && process.env.NODE_ENV !== 'test') {
        const pollStart = Date.now()
        while (Date.now() - pollStart < 45000) {
          await new Promise(resolve => setTimeout(resolve, 1500))
          const polledCase = await CasesService.get(activeCaseNum)
          const pending = polledCase?.pendingExtractions || polledCase?.extensions?.pendingExtractions
          const matchesJob = targetJobId ? pending?.jobId && String(pending.jobId).toLowerCase() === targetJobId : true
          const isFresh = pending?.uploadedAt ? new Date(pending.uploadedAt).getTime() >= startTime - 2000 : true
          if (
            pending &&
            matchesJob &&
            isFresh &&
            (pending.extractedData ||
              pending.status === 'Completed' ||
              (pending.fields && Object.keys(pending.fields).length > 0))
          ) {
            completedPending = pending
            updatedCaseModel = polledCase
            break
          }
          if (pending?.status === 'Failed' && matchesJob) {
            break
          }
        }
      }

      if (updatedCaseModel) {
        setHitlCaseModel(updatedCaseModel)
      }
      if (completedPending) {
        if (completedPending.documents && completedPending.documents.length > 0) {
          setSmartUploadDocuments(
            completedPending.documents.map((doc, idx) => ({
              ...doc,
              id: idx === 0 ? localDoc.id : doc.id || `doc-${idx + 1}`,
              fileName: idx === 0 ? localDoc.fileName : doc.fileName,
              fileType: idx === 0 ? localDoc.fileType : doc.fileType,
              url: idx === 0 ? localDoc.url || doc.url : doc.url || localDoc.url,
              blob: idx === 0 ? file : null,
            })),
          )
        }
        setIsAnalyzingSmartUpload(false)
        cogoToast.success('Scanned document analyzed successfully!', { position: 'top-right' })
      } else {
        setIsAnalyzingSmartUpload(false)
        cogoToast.success('Scanned document uploaded and bound to appointment!', { position: 'top-right' })
      }
    } catch (err) {
      setIsAnalyzingSmartUpload(false)
      cogoToast.error(`Upload error: ${err.message || 'Failed to process document'}`, { position: 'top-right' })
    }
  }

  const getValidationSchema = (isComplete = false) => {
    return validation.object({
      title: validation.name('Event title').required(),
      caseNumber: validation.shortText('Client').required(),
      provider: validation.shortText('Provider Organization').nullable(),
      individualProvider: validation.shortText('Individual Provider').nullable(),
      description: validation.note('Description').nullable(),
      address: validation.address('Appointment address'),
      transportationServiceId: validation.shortText('Transportation service').nullable(),
      translationServiceId: validation.shortText('Translation service').nullable(),
      assignedToUserId: validation.guid('Assigned To').nullable(),
      activities: validation.arrayOf(validation.activity('Activity', isComplete)),
      expenses: validation.arrayOf(validation.expense('Expense', isComplete)),
      tasks: validation
        .arrayOf(
          validation.object({
            title: validation.name('Task title').required(),
            dueDateUtc: validation.date('Due Date').nullable(),
          }),
        )
        .nullable(),
    })
  }

  const validationSchemaProxy = new Proxy(
    {},
    {
      get: (target, prop) => {
        const currentSchema = getValidationSchema(appointment.complete)
        const val = currentSchema[prop]
        return typeof val === 'function' ? val.bind(currentSchema) : val
      },
    },
  )

  const {
    createInputProps,
    getValue,
    setModelValue,
    setModelValues,
    ErrorMessage,
    handleBlur,
    getDateTimeErrors,
    getUtcDateTime,
    isValidTimeRange,
    addNestedItemToArray,
    removeNestedItemFromArray,
  } = createFormHelpers({
    setModelFunction: setAppointment,
    touchedObject: touched,
    setTouchedFunction: setTouched,
    submitAttempted,
    validationSchema: validationSchemaProxy,
  })

  // Effect 1: Load case data, providers, translation/transportation services
  useEffect(() => {
    if (!caseNumber) {
      // New Appointment from global menu (no case selected yet)
      // Load all cases for the dropdown
      const loadAllCases = async () => {
        setIsLoading(true)
        try {
          const allCases = await CasesService.getAll(['caseNumber', 'client'])
          setCases(allCases)
          setAppointment(prev => ({ ...prev, assignedToUserId: currentUser.id }))
          setTranslationServices([])
          setTransportationServices([])
        } catch (err) {
          setError(err.message)
        } finally {
          setIsLoading(false)
        }
      }
      loadAllCases()
      return
    }

    const loadCaseData = async () => {
      // Only set loading if case is not already loaded to prevent flashing
      const isCaseLoaded = cases.length > 0 && String(cases[0].caseNumber) === String(caseNumber)
      if (!isCaseLoaded) {
        setIsLoading(true)
      }
      try {
        const caseObj = await CasesService.get(caseNumber)
        setCases([caseObj])
        setClientName(
          `${getValue('client.firstName', '', caseObj).toUpperCase()} ${getValue(
            'client.lastName',
            '',
            caseObj,
          ).toUpperCase()}`,
        )

        // Load Providers for this case
        const providers = await ProviderService.getProvidersForCase(caseNumber)
        setTreatmentProviders(providers || [])

        // Load Translation & Transportation Services from Case
        if (caseObj && caseObj.translationServices) {
          setTranslationServices(caseObj.translationServices.map(x => ({ id: x.providerId, name: x.providerName })))
        } else {
          setTranslationServices([])
        }

        if (caseObj && caseObj.transportation) {
          setTransportationServices(caseObj.transportation.map(x => ({ id: x.providerId, name: x.providerName })))
        } else {
          setTransportationServices([])
        }

        if (!appointmentNumber) {
          setAppointment(prev => {
            // Keep existing fields if we already set them (e.g. from form inputs before first autosave)
            // but ensure caseNumber and assignedToUserId are set
            return {
              ...prev,
              caseNumber,
              assignedToUserId: prev.assignedToUserId || currentUser.id,
            }
          })
        }
      } catch (err) {
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    loadCaseData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber])

  // Effect 2: Load appointment data when appointmentNumber changes
  useEffect(() => {
    if (!caseNumber) {
      return
    }

    if (!appointmentNumber) {
      const searchParams = new URLSearchParams(location?.search || '')
      const duplicateFromParam = searchParams.get('duplicateFrom')
      const stateDuplicateAppt = location?.state?.duplicateAppointment

      const applyDuplicatedSource = sourceAppt => {
        const cloned = buildDuplicatedAppointment(
          { ...sourceAppt, caseNumber: sourceAppt?.caseNumber || caseNumber },
          currentUser,
        )
        const followUpSuggestion = detectFollowUpSuggestion(sourceAppt)
        setAppointment(cloned)
        setAppointmentDateAndTime({
          date: null,
          startTime: '',
          endTime: '',
        })
        setDuplicatedFromInfo({
          sourceAppointmentNumber: sourceAppt?.appointmentNumber || duplicateFromParam,
          sourceTitle: sourceAppt?.title || 'prior appointment',
          followUpSuggestion,
        })
        setError('')
        setSubmitAttempted(false)
        setTouched({})
        setNewNote('')
        setTimeout(() => {
          setFocusById('date')
        }, 120)
      }

      if (stateDuplicateAppt) {
        applyDuplicatedSource(stateDuplicateAppt)
        return
      }

      if (duplicateFromParam) {
        setIsLoading(true)
        AppointmentService.get(caseNumber, duplicateFromParam)
          .then(sourceAppt => {
            applyDuplicatedSource(sourceAppt)
          })
          .catch(err => {
            setError(err.message || 'Failed to load appointment to duplicate')
          })
          .finally(() => {
            setIsLoading(false)
          })
        return
      }

      setDuplicatedFromInfo(null)
      setAppointment({
        caseNumber,
        assignedToUserId: currentUser.id,
        complete: false,
        title: '',
        provider: '',
        individualProvider: '',
        notes: [],
        activities: [],
        expenses: [],
      })
      setAppointmentDateAndTime({
        date: null,
        startTime: '',
        endTime: '',
      })
      setError('')
      setSubmitAttempted(false)
      setTouched({})
      setNewNote('')
      return
    }

    setDuplicatedFromInfo(null)

    // Check if the currently held appointment already matches the target appointmentNumber
    if (
      (appointment && appointment.appointmentNumber === parseInt(appointmentNumber, 10)) ||
      (createdAppointmentNumberRef.current && createdAppointmentNumberRef.current === parseInt(appointmentNumber, 10))
    ) {
      createdAppointmentNumberRef.current = null
      return
    }

    const loadAppointmentData = async () => {
      setIsLoading(true)
      try {
        const appt = await AppointmentService.get(caseNumber, appointmentNumber)
        if (appt && appt.startUtc) {
          if (appt.isAllDay) {
            setAppointmentDateAndTime({
              date: Moment.utc(appt.startUtc).format('YYYY-MM-DD'),
              startTime: '',
              endTime: '',
            })
          } else {
            const mLocal = Moment.utc(appt.startUtc).local()
            setAppointmentDateAndTime({
              date: mLocal.format('YYYY-MM-DD'),
              startTime: mLocal.format(),
              endTime: appt.endUtc ? Moment.utc(appt.endUtc).local().format() : '',
            })
          }
        } else {
          setAppointmentDateAndTime({
            date: null,
            startTime: '',
            endTime: '',
          })
        }
        const activities = getValue('activities', [], appt)

        activities.forEach(act => {
          if (!act.clientActivityId) {
            act.clientActivityId = act.activityNumber
              ? `act-${act.activityNumber}`
              : `new-${Math.random().toString(36).substr(2, 9)}`
          }
        })

        const expenses = getValue('expenses', [], appt)
        expenses.forEach(exp => {
          if (!exp.clientActivityId && exp.activityNumber) {
            exp.clientActivityId = `act-${exp.activityNumber}`
          }
        })

        setAppointment(appt)
      } catch (err) {
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    loadAppointmentData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseNumber, appointmentNumber, location?.search])

  const handleDuplicateAppointment = () => {
    const activeCaseNum = caseNumber || appointment?.caseNumber
    if (!activeCaseNum) return
    const sourceNum = appointmentNumber || appointment?.appointmentNumber
    const sourceWithCurrentDraftNote = newNote?.trim()
      ? {
          ...appointment,
          notes: [...(appointment.notes || []), { note: newNote.trim() }],
        }
      : appointment

    history.push({
      pathname: `/cases/${activeCaseNum}/appointments`,
      search: sourceNum ? `?duplicateFrom=${sourceNum}` : '',
      state: {
        duplicateAppointment: sourceWithCurrentDraftNote,
        from: location?.state?.from || `/cases/${activeCaseNum}`,
      },
    })
  }

  const handleApplyFollowUpSuggestion = suggestedDateStr => {
    if (!suggestedDateStr) return
    setAppointmentDateAndTime(prev => ({
      ...prev,
      date: suggestedDateStr,
    }))
    cogoToast.info(`Applied suggested follow-up date (${Moment(suggestedDateStr).format('MM/DD/YYYY')}).`, {
      position: 'top-right',
    })
  }

  const stateRef = useRef()
  stateRef.current = { appointment, newNote, caseNumber, appointmentNumber, currentUser }

  useEffect(() => {
    return () => {
      const current = stateRef.current
      if (!current) return

      const appointmentCaseNumber = caseNumber || current.appointment?.caseNumber

      // Save unsaved notes on unmount
      if (current.newNote && current.newNote.trim() && current.appointmentNumber && appointmentCaseNumber) {
        const note = {
          id: 0,
          appointmentNumber: parseInt(current.appointmentNumber, 10),
          caseNumber: parseInt(appointmentCaseNumber, 10),
          note: current.newNote,
          createdUtc: new Date().toISOString(),
          createdBy: current.currentUser ? `${current.currentUser.firstName} ${current.currentUser.lastName}` : 'User',
        }
        const updatedAppt = {
          ...current.appointment,
          notes: [...(current.appointment?.notes || []), note],
        }
        AppointmentService.update(appointmentCaseNumber, current.appointmentNumber, updatedAppt).catch(e => {
          console.error('Failed to save note on unmount:', e)
        })
      }

      // Save unsaved tasks on unmount
      if (current.appointment?.tasks && appointmentCaseNumber) {
        const unsavedTasks = (current.appointment.tasks || []).filter(
          t => !t.taskNumber && !t.isSaved && t.title.trim(),
        )
        if (unsavedTasks.length > 0) {
          unsavedTasks.forEach(t => {
            const newTask = {
              caseNumber: parseInt(appointmentCaseNumber, 10),
              title: t.title,
              dueDateUtc: t.dueDateUtc,
              complete: false,
              description: '',
              note: '',
              activities: [],
              expenses: [],
            }
            TasksService.create(newTask, appointmentCaseNumber).catch(e => {
              console.error('Failed to save task on unmount:', e)
            })
          })
        }
      }
    }
  }, [caseNumber])

  const handleClickExit = async e => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault()
    }

    const appointmentCaseNumber = caseNumber || getValue('caseNumber', '', appointment)
    if (newNote && newNote.trim() && appointmentNumber && appointmentCaseNumber) {
      const note = {
        id: 0,
        appointmentNumber: parseInt(appointmentNumber, 10),
        caseNumber: parseInt(appointmentCaseNumber, 10),
        note: newNote,
        createdUtc: new Date().toISOString(),
        createdBy: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User',
      }
      try {
        const updatedAppt = {
          ...appointment,
          notes: [...(appointment.notes || []), note],
        }
        await AppointmentService.update(appointmentCaseNumber, appointmentNumber, updatedAppt)
      } catch (e) {
        console.error('Failed to save note on exit:', e)
      }
    }

    // Save any unsaved tasks before exiting
    const unsavedTasks = (appointment.tasks || []).filter(t => !t.taskNumber && !t.isSaved && t.title.trim())
    if (unsavedTasks.length > 0 && appointmentCaseNumber) {
      const taskPromises = unsavedTasks.map(t => {
        const newTask = {
          caseNumber: parseInt(appointmentCaseNumber, 10),
          title: t.title,
          dueDateUtc: t.dueDateUtc,
          complete: false,
          description: '',
          note: '',
          activities: [],
          expenses: [],
        }
        return TasksService.create(newTask, appointmentCaseNumber)
      })
      try {
        await Promise.all(taskPromises)
      } catch (e) {
        console.error('Failed to save tasks on exit:', e)
      }
    }

    if (refreshCaseStatus) {
      refreshCaseStatus()
    }
    const fromPath = location.state?.from
    if (fromPath) {
      history.push(fromPath)
    } else if (appointmentCaseNumber) {
      history.push(`/cases/${appointmentCaseNumber}`)
    } else {
      history.push('/')
    }
  }

  const handleDeleteAppointment = async (force = false) => {
    setError('')
    try {
      await AppointmentService.deleteAppointment(caseNumber, appointmentNumber, force)
      if (refreshCaseStatus) {
        refreshCaseStatus()
      }
      const fromPath = location.state?.from
      if (fromPath) {
        history.push(fromPath)
      } else {
        history.push(`/cases/${caseNumber}`)
      }
    } catch (err) {
      if (err.requiresForce) {
        setDeleteForceWarning(err.serverMessage)
      } else {
        setError(err.message || 'Failed to delete appointment')
      }
    }
  }

  const isUpcoming = (() => {
    const startUtc = getUtcDateTime('startTime', appointmentDateAndTime)
    if (!startUtc) return false
    if (!appointmentDateAndTime.startTime) {
      const dateStr = appointmentDateAndTime.date
        ? Moment(appointmentDateAndTime.date).format('YYYY-MM-DD')
        : Moment.utc(startUtc).format('YYYY-MM-DD')
      return Moment().local().isSameOrBefore(Moment(dateStr, 'YYYY-MM-DD').endOf('day'))
    }
    return new Date() < new Date(startUtc)
  })()

  const isCompletable = (() => {
    if (appointment.complete) return true
    if (isUpcoming) return false
    const activities = getValue('activities', [], appointment)
    if (activities.length === 0) return false
    return !activities.some(a => !a.time || parseFloat(a.time) <= 0)
  })()

  const handleCompleteAppointment = () => {
    setSubmitAttempted(true)

    const appointmentCaseNumber = caseNumber || getValue('caseNumber', '', appointment)
    const startUtc = getUtcDateTime('startTime', appointmentDateAndTime)
    const endUtc = getUtcDateTime('endTime', appointmentDateAndTime)

    if (!!appointmentCaseNumber && !!startUtc && areFieldsValid(true) && isValidTimeRange(startUtc, endUtc)) {
      const completedAppointment = { ...appointment }
      completedAppointment.caseNumber = appointmentCaseNumber
      completedAppointment.startUtc = startUtc
      completedAppointment.endUtc = endUtc
      completedAppointment.isAllDay = Boolean(appointmentDateAndTime.date) && !appointmentDateAndTime.startTime

      if (newNote && newNote.trim()) {
        const note = {
          id: 0,
          appointmentNumber: parseInt(appointmentNumber, 10),
          caseNumber: parseInt(appointmentCaseNumber, 10),
          note: newNote,
          createdUtc: new Date().toISOString(),
          createdBy: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User',
        }
        completedAppointment.notes = [...(completedAppointment.notes || []), note]
      }

      if (completedAppointment.activities) {
        completedAppointment.activities.forEach(a => {
          a.caseNumber = parseInt(appointmentCaseNumber, 10)
          if (!a.userId || a.userId === '') {
            a.userId = null
          }
        })
      }
      if (completedAppointment.expenses) {
        completedAppointment.expenses.forEach(e => {
          e.caseNumber = parseInt(appointmentCaseNumber, 10)
          if (!e.userId || e.userId === '') {
            e.userId = null
          }
        })
      }

      completedAppointment.complete = true

      const tasksToSave = (completedAppointment.tasks || []).filter(t => !t.taskNumber && !t.isSaved && t.title.trim())
      completedAppointment.tasks = []

      setIsLoading(true)
      const savePromise = appointmentNumber
        ? AppointmentService.update(appointmentCaseNumber, appointmentNumber, completedAppointment)
        : AppointmentService.create(appointmentCaseNumber, completedAppointment)

      savePromise
        .then(async () => {
          if (tasksToSave.length > 0) {
            const taskPromises = tasksToSave.map(t => {
              const newTask = {
                caseNumber: parseInt(appointmentCaseNumber, 10),
                title: t.title,
                dueDateUtc: t.dueDateUtc,
                complete: false,
                description: '',
                note: '',
                activities: [],
                expenses: [],
              }
              return TasksService.create(newTask, appointmentCaseNumber)
            })
            await Promise.all(taskPromises)
          }
        })
        .then(() => {
          setIsLoading(false)
          setError(null)
          handleClickExit()
        })
        .catch(() => {
          setIsLoading(false)
          setError('An unexpected error occurred while trying to complete the appointment')
        })
    } else {
      const hasActivities = getValue('activities', [], appointment).length !== 0
      if (!hasActivities) {
        setShowRequiredActivitiesError(true)
      }
      if (!startUtc) {
        cogoToast.error('Please enter an appointment date and start time.', { position: 'top-right' })
      } else if (!isValidTimeRange(startUtc, endUtc)) {
        cogoToast.error('Appointment end time must be after start time.', { position: 'top-right' })
      } else if (!areFieldsValid(true)) {
        const validationResult = validation.validate(appointment, getValidationSchema(true))
        const errorMessages = Object.values(validationResult?.errors || {})
        const firstError =
          errorMessages.find(Boolean) || 'Please resolve the form errors before completing the appointment.'
        cogoToast.error(firstError, { position: 'top-right' })
      }
    }
  }

  const handleClickSave = () => {
    setSubmitAttempted(true)

    const appointmentCaseNumber = caseNumber || getValue('caseNumber', '', appointment)
    const startUtc = getUtcDateTime('startTime', appointmentDateAndTime)
    const endUtc = getUtcDateTime('endTime', appointmentDateAndTime)

    const isComplete = !!appointment?.complete
    const hasStartTime = !!getValue('startTime', null, appointmentDateAndTime)
    const hasValidDate = !isComplete
      ? !startUtc || (!!startUtc && isValidTimeRange(startUtc, endUtc))
      : !!startUtc && hasStartTime && isValidTimeRange(startUtc, endUtc)

    const isValid = areFieldsValid(isComplete)

    if (!!appointmentCaseNumber && hasValidDate && isValid) {
      const appointmentObject = { ...appointment }
      appointmentObject.caseNumber = appointmentCaseNumber
      appointmentObject.startUtc = startUtc || null
      appointmentObject.endUtc = endUtc || null
      appointmentObject.isAllDay = Boolean(appointmentDateAndTime.date) && !appointmentDateAndTime.startTime

      const hasRealActivities =
        appointmentObject.activities &&
        appointmentObject.activities.length > 0 &&
        appointmentObject.activities.some(
          a =>
            Boolean(a.activityNumber) ||
            Boolean(a.type && a.type.trim() !== '') ||
            Boolean(a.description && a.description.trim() !== '') ||
            Boolean(a.time && parseFloat(a.time) > 0),
        )

      if (!appointmentNumber && !hasRealActivities) {
        const defaultUserId =
          appointmentObject.assignedToUserId ||
          cases[0]?.caseManager ||
          currentUser?.id ||
          caseMemberOptions[0]?.id ||
          ''
        appointmentObject.activities = buildStandardAppointmentActivities(startUtc, defaultUserId)
      }

      if (appointmentObject.activities) {
        appointmentObject.activities.forEach(a => {
          a.caseNumber = parseInt(appointmentCaseNumber, 10)
          if (!a.userId || a.userId === '') {
            a.userId = null
          }
        })
      }
      if (appointmentObject.expenses) {
        appointmentObject.expenses.forEach(e => {
          e.caseNumber = parseInt(appointmentCaseNumber, 10)
          if (!e.userId || e.userId === '') {
            e.userId = null
          }
        })
      }

      if (newNote && newNote.trim()) {
        const note = {
          id: 0,
          appointmentNumber: parseInt(appointmentNumber, 10),
          caseNumber: parseInt(appointmentCaseNumber, 10),
          note: newNote,
          createdUtc: new Date().toISOString(),
          createdBy: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User',
        }
        appointmentObject.notes = [...(appointmentObject.notes || []), note]
      }

      // Filter out tasks that have already been saved to prevent duplication.
      // Send only unsaved tasks to the backend to be created.
      const unsavedTasks = (appointmentObject.tasks || []).filter(t => !t.taskNumber && !t.isSaved && t.title.trim())
      appointmentObject.tasks = unsavedTasks

      const savePromise = appointmentNumber
        ? AppointmentService.update(appointmentCaseNumber, appointmentNumber, appointmentObject)
        : AppointmentService.create(appointmentCaseNumber, appointmentObject)

      savePromise
        .then(res => {
          setError(null)
          if (refreshCaseStatus) refreshCaseStatus()

          if (newNote && newNote.trim()) {
            setNewNote('')
          }

          if (appointment.complete) {
            cogoToast.success('Appointment saved successfully', { position: 'top-right' })
            handleClickExit()
          } else {
            cogoToast.success('Draft saved successfully', { position: 'top-right' })
            if (res && res.appointmentNumber) {
              createdAppointmentNumberRef.current = res.appointmentNumber
              const savedActivities = (res.activities || []).map(act => ({
                ...act,
                clientActivityId:
                  act.clientActivityId ||
                  (act.activityNumber ? `act-${act.activityNumber}` : `new-${Math.random().toString(36).substr(2, 9)}`),
              }))
              setAppointment({
                ...res,
                activities: savedActivities,
                tasks: appointmentObject.tasks,
                notes: res.notes || appointmentObject.notes,
              })
              if (!appointmentNumber) {
                history.replace(`/cases/${appointmentCaseNumber}/appointments/${res.appointmentNumber}`)
              }
            }
          }
        })
        .catch(err => {
          setError(
            `An unexpected error occurred while trying to save the appointment: ${err.message || err.toString()}`,
          )
        })
    } else {
      const validationResult = validation.validate(appointment, getValidationSchema(isComplete))
      const hasActivities = getValue('activities', [], appointment).length !== 0

      if (isComplete && !hasActivities) {
        setShowRequiredActivitiesError(true)
      }

      if (!hasValidDate) {
        if (isComplete && (!startUtc || !hasStartTime)) {
          cogoToast.error('Please enter an appointment date and start time.', { position: 'top-right' })
        } else if (!isValidTimeRange(startUtc, endUtc)) {
          cogoToast.error('Appointment end time must be after start time.', { position: 'top-right' })
        }
      } else if (!validationResult?.success) {
        const errorMessages = Object.values(validationResult?.errors || {})
        const firstError = errorMessages.find(Boolean) || 'Please resolve the form errors before saving.'
        cogoToast.error(firstError, { position: 'top-right' })
      } else if (isComplete && !hasActivities) {
        cogoToast.error('At least one activity is required for a completed appointment.', { position: 'top-right' })
      }
    }
  }

  const debouncedAutosave = useDebouncedCallback((currentAppt, dateTime) => {
    const appointmentCaseNumber = caseNumber || getValue('caseNumber', '', currentAppt)
    const startUtc = getUtcDateTime('startTime', dateTime)
    const endUtc = getUtcDateTime('endTime', dateTime)

    const validationResult = validation.validate(currentAppt, getValidationSchema(false))
    const isValid = !!validationResult && validationResult.success

    const hasValidDate = !appointmentNumber
      ? !!startUtc && isValidTimeRange(startUtc, endUtc)
      : !startUtc || isValidTimeRange(startUtc, endUtc)

    if (!!appointmentCaseNumber && hasValidDate && isValid) {
      const payload = { ...currentAppt }
      payload.caseNumber = appointmentCaseNumber
      payload.startUtc = startUtc || null
      payload.endUtc = endUtc || null
      payload.isAllDay = Boolean(dateTime?.date) && !dateTime?.startTime

      const hasRealActivities =
        payload.activities &&
        payload.activities.length > 0 &&
        payload.activities.some(
          a =>
            Boolean(a.activityNumber) ||
            Boolean(a.type && a.type.trim() !== '') ||
            Boolean(a.description && a.description.trim() !== '') ||
            Boolean(a.time && parseFloat(a.time) > 0),
        )

      if (!appointmentNumber && !hasRealActivities) {
        const defaultUserId =
          payload.assignedToUserId || cases[0]?.caseManager || currentUser?.id || caseMemberOptions[0]?.id || ''
        payload.activities = buildStandardAppointmentActivities(startUtc, defaultUserId)
      }

      if (payload.activities) {
        payload.activities.forEach(a => {
          a.caseNumber = parseInt(appointmentCaseNumber, 10)
          if (!a.userId || a.userId === '') a.userId = null
        })
      }
      if (payload.expenses) {
        payload.expenses.forEach(e => {
          e.caseNumber = parseInt(appointmentCaseNumber, 10)
          if (!e.userId || e.userId === '') e.userId = null
        })
      }

      payload.tasks = []

      if (appointmentNumber) {
        AppointmentService.update(appointmentCaseNumber, appointmentNumber, payload)
          .then(() => {
            if (refreshCaseStatus) refreshCaseStatus()
          })
          .catch(err => console.error('Autosave failed:', err))
      } else {
        AppointmentService.create(appointmentCaseNumber, payload)
          .then(res => {
            if (res && res.appointmentNumber) {
              createdAppointmentNumberRef.current = res.appointmentNumber
              const savedActivities = (res.activities || []).map(act => ({
                ...act,
                clientActivityId:
                  act.clientActivityId ||
                  (act.activityNumber ? `act-${act.activityNumber}` : `new-${Math.random().toString(36).substr(2, 9)}`),
              }))
              setAppointment(prev => ({
                ...res,
                ...prev,
                appointmentNumber: res.appointmentNumber,
                id: res.id,
                activities: savedActivities,
                tasks: prev.tasks || currentAppt.tasks,
                notes: res.notes || currentAppt.notes,
              }))
              if (refreshCaseStatus) refreshCaseStatus()
              history.replace(`/cases/${appointmentCaseNumber}/appointments/${res.appointmentNumber}`)
            }
          })
          .catch(err => console.error('Autosave failed:', err))
      }
    }
  }, 1000)

  useEffect(() => {
    if (isInitialMount.current) {
      if (appointmentNumber) {
        if (appointment.appointmentNumber !== undefined) {
          isInitialMount.current = false
        }
      } else {
        isInitialMount.current = false
      }
      return
    }
    debouncedAutosave(appointment, appointmentDateAndTime)
  }, [appointment, appointmentDateAndTime, debouncedAutosave, appointmentNumber])

  const areFieldsValid = (isComplete = false) => {
    const result = validation.validate(appointment, getValidationSchema(isComplete))
    const hasActivities = getValue('activities', [], appointment).length !== 0

    if (isComplete) {
      return !!result && result.success && !!hasActivities
    }
    return !!result && result.success
  }

  const handleAddExpense = (clientActivityId = null) => {
    const expenses = getValue('expenses', [], appointment)
    const nextIndex = expenses.length
    const userId =
      appointment.assignedToUserId || cases[0]?.caseManager || currentUser?.id || caseMemberOptions[0]?.id || ''

    if (!clientActivityId) {
      setFocusById(`expenses[${nextIndex}].type`)
    }
    addNestedItemToArray(
      'expenses',
      {
        date: appointment.startUtc ? new Date(format.date(appointment.startUtc)) : new Date(),
        type: '',
        amount: 0,
        distance: 0,
        description: '',
        userId,
        clientActivityId,
      },
      appointment,
      setAppointment,
    )
  }

  const handleAddActivity = () => {
    const activities = getValue('activities', [], appointment)
    const nextIndex = activities.length
    const userId =
      appointment.assignedToUserId || cases[0]?.caseManager || currentUser?.id || caseMemberOptions[0]?.id || ''

    setFocusById(`activities[${nextIndex}].type`)
    addNestedItemToArray(
      'activities',
      {
        date: appointment.startUtc ? new Date(format.date(appointment.startUtc)) : new Date(),
        type: '',
        time: '',
        description: '',
        userId,
        sortOrder: nextIndex,
        clientActivityId: `new-${Math.random().toString(36).substr(2, 9)}`,
      },
      appointment,
      setAppointment,
    )
    setShowRequiredActivitiesError(false)
  }

  const handleQuickAddActivityPreset = preset => {
    const activities = getValue('activities', [], appointment)
    const nextIndex = activities.length
    const userId =
      appointment.assignedToUserId || cases[0]?.caseManager || currentUser?.id || caseMemberOptions[0]?.id || ''
    const providerLabel = appointment.individualProvider || appointment.provider || 'Treating Provider'

    addNestedItemToArray(
      'activities',
      {
        date: appointment.startUtc ? new Date(format.date(appointment.startUtc)) : new Date(),
        type: preset.type,
        time: preset.time,
        description: preset.getDescription(providerLabel, appointment.title),
        userId,
        sortOrder: nextIndex,
        clientActivityId: `new-${Math.random().toString(36).substr(2, 9)}`,
      },
      appointment,
      setAppointment,
    )
    setShowRequiredActivitiesError(false)
  }

  const handleAddTask = () => {
    const tasks = getValue('tasks', [], appointment)
    const nextIndex = tasks.length
    setFocusById(`tasks[${nextIndex}].title`)
    addNestedItemToArray('tasks', { title: '', dueDateUtc: null }, appointment, setAppointment)
  }

  const handleRemoveActivity = index => {
    if (getValue('activities', [], appointment).length < 2 && submitAttempted) {
      setShowRequiredActivitiesError(true)
    }
    removeNestedItemFromArray('activities', index, appointment, setAppointment)
  }

  const handleDragStart = (e, index) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e, index) => {
    e.preventDefault()
  }

  const handleDrop = (e, targetIndex) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === targetIndex) return
    handleReorder(draggedIndex, targetIndex)
    setDraggedIndex(null)
  }

  const handleReorder = (fromIndex, toIndex) => {
    const activities = [...getValue('activities', [], appointment)]
    const act1 = activities[fromIndex]
    const act2 = activities[toIndex]
    if (!act1 || !act2) return

    const date1 = act1.date ? new Date(act1.date).toDateString() : ''
    const date2 = act2.date ? new Date(act2.date).toDateString() : ''
    if (date1 !== date2) return

    activities[fromIndex] = act2
    activities[toIndex] = act1

    const groups = {}
    activities.forEach((act, idx) => {
      const dStr = act.date ? new Date(act.date).toDateString() : ''
      if (!groups[dStr]) groups[dStr] = []
      groups[dStr].push({ act, idx })
    })

    Object.keys(groups).forEach(dStr => {
      groups[dStr].forEach((item, order) => {
        activities[item.idx] = {
          ...item.act,
          sortOrder: order,
        }
      })
    })

    setAppointment({ ...appointment, activities })
  }

  const handleAddNote = () => {
    if (!newNote.trim()) return

    const note = {
      id: 0,
      appointmentNumber: appointment.appointmentNumber,
      caseNumber: appointment.caseNumber,
      note: newNote,
      createdUtc: new Date().toISOString(),
      createdBy: currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'User',
    }

    const currentNotes = getValue('notes', [], appointment)
    const updatedNotes = [...currentNotes, note]

    setModelValue('notes', updatedNotes, appointment, setAppointment)
    setNewNote('')
  }

  const handleRemoveNote = index => {
    const currentNotes = getValue('notes', [], appointment)
    const updatedNotes = currentNotes.filter((_, idx) => idx !== index)
    setModelValue('notes', updatedNotes, appointment, setAppointment)
  }

  const handleStartEditNote = (index, currentText) => {
    setEditingNoteIndex(index)
    setEditingNoteText(currentText)
  }

  const handleSaveEditNote = index => {
    if (!editingNoteText.trim()) return
    const currentNotes = getValue('notes', [], appointment)
    const updatedNotes = currentNotes.map((note, idx) => {
      if (idx === index) {
        return { ...note, note: editingNoteText }
      }
      return note
    })
    setModelValue('notes', updatedNotes, appointment, setAppointment)
    setEditingNoteIndex(null)
    setEditingNoteText('')
  }

  const handleCancelEditNote = () => {
    setEditingNoteIndex(null)
    setEditingNoteText('')
  }

  const expenseOnChangeHandler = (e, namePrefix) => {
    const modelPaths = [{ name: e.target.name, value: e.target.value }]

    if (e.target.name.includes('type') && e.target.value === 'Mileage') {
      modelPaths.push({ name: `${namePrefix}.amount`, value: 0 })
    } else if (e.target.name.includes('type')) {
      modelPaths.push({ name: `${namePrefix}.distance`, value: 0 })
    }

    setModelValues(modelPaths, appointment, setAppointment)
  }

  const triggerFormSubmission = () => {
    formRef.current.dispatchEvent(new Event('submit'))
  }

  const handleClientSelectChange = async e => {
    const selectedCaseNumber = e.target.value
    setModelValues(
      [
        { name: 'provider', value: null },
        { name: e.target.name, value: selectedCaseNumber },
      ],
      appointment,
      setAppointment,
    )

    // Fetch data for the selected case
    if (selectedCaseNumber) {
      try {
        const [providers, caseObj] = await Promise.all([
          ProviderService.getProvidersForCase(selectedCaseNumber),
          CasesService.get(selectedCaseNumber),
        ])
        setTreatmentProviders(providers || [])

        if (caseObj) {
          setCases(prev => prev.map(c => (c.caseNumber === caseObj.caseNumber ? caseObj : c)))
          if (caseObj.translationServices) {
            setTranslationServices(caseObj.translationServices.map(x => ({ id: x.providerId, name: x.providerName })))
          } else {
            setTranslationServices([])
          }

          if (caseObj.transportation) {
            setTransportationServices(caseObj.transportation.map(x => ({ id: x.providerId, name: x.providerName })))
          } else {
            setTransportationServices([])
          }
        }
      } catch (_error) {
        setTreatmentProviders([])
        setTranslationServices([])
        setTransportationServices([])
      }
    } else {
      setTreatmentProviders([])
      setTranslationServices([])
      setTransportationServices([])
    }
  }

  const clientOptions = cases
    ? cases.map(caseObj => ({
        id: getValue('caseNumber', '', caseObj),
        name: `${getValue('caseNumber', '', caseObj)} - ${getValue('client.firstName', '', caseObj)} ${getValue(
          'client.lastName',
          '',
          caseObj,
        )}`,
      }))
    : []

  const formatIndividualName = p => {
    if (
      p.classification === 'Individual' ||
      p.classification === 'SoleProprietor' ||
      (!p.classification && (p.firstName || p.lastName))
    ) {
      const parts = [`${p.firstName || ''} ${p.lastName || ''}`.trim() || p.name]
      if (p.suffix) parts.push(p.suffix)
      if (p.specialties) parts.push(p.specialties)
      return parts.filter(Boolean).join(', ')
    }
    return p.name
  }

  const providerOptions = []
  if (treatmentProviders) {
    treatmentProviders.forEach(provider => {
      const isIndividual =
        provider.classification === 'Individual' ||
        provider.classification === 'SoleProprietor' ||
        (!provider.classification && (provider.firstName || provider.lastName))

      if (!isIndividual && provider.name) {
        // Pure organization
        if (!providerOptions.some(o => o.id === provider.name)) {
          providerOptions.push({
            id: provider.name,
            name: provider.name,
          })
        }
      }

      // If individual or sole proprietor has a parent organization, company name, or affiliations
      const parentOrgNames = [
        provider.parentProvider?.name,
        provider.organizationName && provider.organizationName !== provider.name ? provider.organizationName : null,
        ...(Array.isArray(provider.affiliations) ? provider.affiliations.map(a => a?.organizationName) : []),
      ].filter(Boolean)

      parentOrgNames.forEach(parentOrgName => {
        const trimmed = parentOrgName.trim()
        if (trimmed && !providerOptions.some(o => o.id.toLowerCase() === trimmed.toLowerCase())) {
          providerOptions.push({
            id: trimmed,
            name: trimmed,
          })
        }
      })
    })
  }

  const appointmentDateTimeErrors = getDateTimeErrors(appointmentDateAndTime, appointment?.complete)

  // Find the selected provider object(s) and enforce strict Organization <-> Individual linking
  const selectedProviderName = getValue('provider', '', appointment)
  const selectedIndividualProviderName = getValue('individualProvider', '', appointment)

  const getLinkedOrgNamesForIndividual = (indiv, allProviders = []) => {
    if (!indiv) return []
    const orgNames = new Set()
    const addOrg = name => {
      if (name && typeof name === 'string' && name.trim()) {
        orgNames.add(name.trim())
      }
    }

    if (indiv.classification !== 'SoleProprietor') {
      if (indiv.parentProvider?.name) addOrg(indiv.parentProvider.name)
      if (indiv.organizationName && indiv.organizationName !== indiv.name) {
        addOrg(indiv.organizationName)
      }
      if (indiv.parentProviderId && Array.isArray(allProviders)) {
        const parentObj = allProviders.find(
          p => p.id && String(p.id).toLowerCase() === String(indiv.parentProviderId).toLowerCase(),
        )
        if (parentObj?.name) addOrg(parentObj.name)
      }
      if (Array.isArray(indiv.affiliations)) {
        indiv.affiliations.forEach(aff => {
          if (aff?.organizationName) addOrg(aff.organizationName)
          if (aff?.organizationId && Array.isArray(allProviders)) {
            const affOrg = allProviders.find(
              p => p.id && String(p.id).toLowerCase() === String(aff.organizationId).toLowerCase(),
            )
            if (affOrg?.name) addOrg(affOrg.name)
          }
        })
      }
      if (Array.isArray(allProviders)) {
        const indivFormatted = formatIndividualName(indiv)
        const indivShortName = `${indiv.firstName || ''} ${indiv.lastName || ''}`.trim()
        allProviders.forEach(p => {
          if (Array.isArray(p.individuals) && p.name) {
            const matchesNested = p.individuals.some(nested => {
              const nestedFormatted = formatIndividualName(nested)
              const nestedShort = `${nested.firstName || ''} ${nested.lastName || ''}`.trim()
              return (
                (nested.id && indiv.id && String(nested.id).toLowerCase() === String(indiv.id).toLowerCase()) ||
                (nestedFormatted && nestedFormatted === indivFormatted) ||
                (nestedShort && nestedShort === indivShortName)
              )
            })
            if (matchesNested) addOrg(p.name)
          }
        })
      }
    }

    return Array.from(orgNames)
  }

  const isIndividualLinkedToOrgName = (indiv, orgName, allProviders = []) => {
    if (!orgName || !orgName.trim()) return true
    const target = orgName.trim().toLowerCase()
    const linkedOrgs = getLinkedOrgNamesForIndividual(indiv, allProviders)
    return linkedOrgs.some(o => o.toLowerCase() === target)
  }

  const selectedOrgProvider = selectedProviderName
    ? (treatmentProviders || []).find(
        p =>
          p.name &&
          p.name.trim().toLowerCase() === selectedProviderName.trim().toLowerCase() &&
          p.classification !== 'Individual' &&
          p.classification !== 'SoleProprietor',
      ) ||
      (treatmentProviders || []).find(
        p => p.name && p.name.trim().toLowerCase() === selectedProviderName.trim().toLowerCase(),
      )
    : null

  const selectedIndProvider = selectedIndividualProviderName
    ? (treatmentProviders || []).find(
        p =>
          formatIndividualName(p) === selectedIndividualProviderName ||
          ((p.classification === 'SoleProprietor' || !p.organizationName) && p.name === selectedIndividualProviderName),
      )
    : null

  const selectedProvider =
    selectedIndProvider ||
    selectedOrgProvider ||
    (selectedProviderName
      ? (treatmentProviders || []).find(p => p.parentProvider?.name === selectedProviderName)
      : null)

  const individualProviderOptions = []
  if (selectedProviderName) {
    // Organization is selected: strictly only include Individual Providers linked to this Organization
    if (treatmentProviders) {
      treatmentProviders.forEach(p => {
        const isIndividual =
          p.classification === 'Individual' ||
          p.classification === 'SoleProprietor' ||
          (!p.classification && (p.firstName || p.lastName))

        if (isIndividual && isIndividualLinkedToOrgName(p, selectedProviderName, treatmentProviders)) {
          const opt = {
            id: formatIndividualName(p),
            name: formatIndividualName(p),
          }
          if (opt.id && !individualProviderOptions.some(o => o.id === opt.id)) {
            individualProviderOptions.push(opt)
          }
        }
      })
    }
    if (selectedOrgProvider && Array.isArray(selectedOrgProvider.individuals)) {
      selectedOrgProvider.individuals.forEach(indiv => {
        const formatted = formatIndividualName(indiv) || `${indiv.firstName || ''} ${indiv.lastName || ''}`.trim()
        if (formatted && !individualProviderOptions.some(o => o.id === formatted)) {
          individualProviderOptions.push({
            id: formatted,
            name: formatted,
          })
        }
      })
    }
  } else if (treatmentProviders) {
    treatmentProviders.forEach(provider => {
      const isIndividual =
        provider.classification === 'Individual' ||
        provider.classification === 'SoleProprietor' ||
        (!provider.classification && (provider.firstName || provider.lastName))

      if (isIndividual) {
        const option = {
          id: formatIndividualName(provider),
          name: formatIndividualName(provider),
        }
        if (option.id && !individualProviderOptions.some(o => o.id === option.id)) {
          individualProviderOptions.push(option)
        }
      } else if (provider.individuals && Array.isArray(provider.individuals)) {
        provider.individuals.forEach(indiv => {
          const formatted = `${indiv.firstName || ''} ${indiv.lastName || ''}`.trim()
          if (formatted && !individualProviderOptions.some(o => o.id === formatted)) {
            individualProviderOptions.push({
              id: formatted,
              name: formatted,
            })
          }
        })
      }
    })
  }
  const combinedAddresses = [
    ...(selectedIndProvider?.addresses || []),
    ...(selectedOrgProvider?.addresses || []),
    ...(!selectedIndProvider && !selectedOrgProvider && selectedProvider?.addresses ? selectedProvider.addresses : []),
  ].filter(
    (addr, idx, arr) =>
      addr &&
      addr.line1 &&
      arr.findIndex(a => a.line1 === addr.line1 && a.city === addr.city && a.state === addr.state) === idx,
  )

  const providerAddressOptions = combinedAddresses.map((addr, index) => ({
    id: index.toString(),
    name: `${addr.line1}, ${addr.city} ${addr.state}`,
    value: addr,
  }))

  const activeCaseNumber = appointment.caseNumber || caseNumber
  const selectedCaseObj = cases && cases.find(c => String(c.caseNumber) === String(activeCaseNumber))
  const caseMemberOptions =
    selectedCaseObj && selectedCaseObj.caseMembers
      ? selectedCaseObj.caseMembers.map(cm => ({
          id: cm.id,
          name: `${cm.firstName} ${cm.lastName} (${cm.role})`,
        }))
      : []

  // Ensure current user is in the options (if not already)
  if (currentUser && currentUser.id && !caseMemberOptions.some(m => m.id === currentUser.id)) {
    caseMemberOptions.unshift({
      id: currentUser.id,
      name: `${currentUser.firstName} ${currentUser.lastName} (You)`,
    })
  }

  const applyProviderAddress = addr => {
    if (!addr) return
    setAppointment(prev => ({
      ...prev,
      address: {
        line1: addr.line1 ?? '',
        city: addr.city ?? '',
        state: addr.state ?? '',
        zip: addr.zip ?? '',
      },
    }))
  }

  const handleProviderChange = e => {
    const selectedName = e.target.value ?? ''
    const providerObj = (treatmentProviders || []).find(
      p => p.name && p.name.trim().toLowerCase() === String(selectedName).trim().toLowerCase(),
    )

    // Auto-prefill with primary address (or first address) on provider selection
    const primaryAddr = providerObj?.addresses?.length
      ? providerObj.addresses.find(a => a.type === 'PrimaryPractice') ?? providerObj.addresses[0]
      : null

    setAppointment(prev => {
      const currentIndivName = prev.individualProvider || ''
      const currentIndivObj = currentIndivName
        ? (treatmentProviders || []).find(
            p => formatIndividualName(p) === currentIndivName || p.name === currentIndivName,
          )
        : null
      const keepIndividual =
        !selectedName ||
        (currentIndivObj && isIndividualLinkedToOrgName(currentIndivObj, selectedName, treatmentProviders))

      return {
        ...prev,
        provider: selectedName,
        individualProvider: keepIndividual ? currentIndivName : '',
        ...(primaryAddr && {
          address: {
            line1: primaryAddr.line1 ?? '',
            city: primaryAddr.city ?? '',
            state: primaryAddr.state ?? '',
            zip: primaryAddr.zip ?? '',
          },
        }),
      }
    })
  }

  const handleIndividualProviderChange = e => {
    const selectedName = e.target.value ?? ''
    if (!selectedName) {
      setAppointment(prev => ({
        ...prev,
        individualProvider: '',
      }))
      return
    }

    const selectedIndividual = (treatmentProviders || []).find(
      p => formatIndividualName(p) === selectedName || p.name === selectedName,
    )
    const linkedOrgNames = getLinkedOrgNamesForIndividual(selectedIndividual, treatmentProviders)

    if (selectedIndividual?.classification === 'SoleProprietor' || linkedOrgNames.length === 0) {
      const primaryAddr = selectedIndividual?.addresses?.length
        ? selectedIndividual.addresses.find(a => a.type === 'PrimaryPractice') ?? selectedIndividual.addresses[0]
        : null

      setAppointment(prev => ({
        ...prev,
        provider: '',
        individualProvider: selectedName,
        ...(primaryAddr && {
          address: {
            line1: primaryAddr.line1 ?? '',
            city: primaryAddr.city ?? '',
            state: primaryAddr.state ?? '',
            zip: primaryAddr.zip ?? '',
          },
        }),
      }))
    } else {
      const parentProvider =
        selectedIndividual?.parentProvider ||
        (selectedIndividual?.parentProviderId
          ? (treatmentProviders || []).find(p => p.id === selectedIndividual.parentProviderId)
          : null) ||
        (treatmentProviders || []).find(
          p => p.name && linkedOrgNames.some(o => o.toLowerCase() === p.name.trim().toLowerCase()),
        )

      const addrSource = selectedIndividual?.addresses?.length ? selectedIndividual : parentProvider
      const primaryAddr = addrSource?.addresses?.length
        ? addrSource.addresses.find(a => a.type === 'PrimaryPractice') ?? addrSource.addresses[0]
        : null

      setAppointment(prev => {
        const currentOrgMatches =
          prev.provider && linkedOrgNames.some(o => o.toLowerCase() === prev.provider.trim().toLowerCase())
        const nextProviderOrg = currentOrgMatches ? prev.provider : linkedOrgNames[0] || ''

        return {
          ...prev,
          individualProvider: selectedName,
          provider: nextProviderOrg,
          ...(primaryAddr &&
            !prev.address?.line1 && {
              address: {
                line1: primaryAddr.line1 || '',
                city: primaryAddr.city || '',
                state: primaryAddr.state || '',
                zip: primaryAddr.zip || '',
              },
            }),
        }
      })
    }
  }

  const handleProviderAddressSelect = e => {
    const selectedAddrIdx = e.target.value
    const addr = combinedAddresses[selectedAddrIdx]
    applyProviderAddress(addr)
  }

  const handleTranslationSave = (model, isUpdate) => {
    return isUpdate ? TranslationServicesService.update(model) : TranslationServicesService.create(model)
  }

  const handleTranslationSaveSuccess = res => {
    TranslationServicesService.getAll().then(setTranslationServices)
    setAppointment(prev => ({ ...prev, translationServiceId: res.id }))
    setShowTranslationModal(false)
    setServiceToEdit(null)
  }

  const handleTransportationSave = (model, isUpdate) => {
    return isUpdate ? TransportationServicesService.update(model) : TransportationServicesService.create(model)
  }

  const handleTransportationSaveSuccess = res => {
    TransportationServicesService.getAll().then(setTransportationServices)
    setAppointment(prev => ({ ...prev, transportationServiceId: res.id }))
    setShowTransportationModal(false)
    setServiceToEdit(null)
  }

  const getServiceOldModel = (id, list) => {
    if (!id) return {}
    return list.find(x => x.id === id) || {}
  }

  return (
    <React.Fragment>
      <Helmet title={clientName ? `Appointment - ${clientName}` : 'New Appointment'} />
      <Slide className="new-appointment-container background" direction="up" in={true} mountOnEnter unmountOnExit>
        <div>
          {isInline ? (
            <h4 className="mt-3 mb-3 text-primary font-weight-bold" style={{ paddingLeft: '15px' }}>
              {isLoading || !appointment
                ? 'Appointment'
                : appointment.complete
                ? 'Completed Appointment'
                : 'Upcoming Appointment'}
            </h4>
          ) : caseNumber !== undefined ? (
            <CaseNavigationBar
              title={clientName}
              subtitle={`CASE NO: ${caseNumber}`}
              rightButtonText={'SAVE'}
              handleClickLeft={handleClickExit}
              handleRightClick={triggerFormSubmission}
              shadow="shadow"
              caseNumber={caseNumber}
              isInline="sticky"
            />
          ) : (
            <NavigationBar
              title="NEW APPOINTMENT"
              rightBtnText="SAVE"
              leftBtnText="Cancel"
              handleClickLeft={handleClickExit}
              handleClickRight={triggerFormSubmission}
              isInline="sticky"
            />
          )}
          {!!deleteForceWarning && (
            <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Delete Appointment</h5>
                  </div>
                  <div className="modal-body">
                    <p>{deleteForceWarning}</p>
                    <p>Are you sure you want to permanently delete this appointment and all associated data?</p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setDeleteForceWarning('')}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        setDeleteForceWarning('')
                        handleDeleteAppointment(true)
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {showDeleteConfirm && (
            <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Delete Appointment</h5>
                  </div>
                  <div className="modal-body">
                    <p>Are you sure you want to permanently delete this appointment and all associated data?</p>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => {
                        setShowDeleteConfirm(false)
                        handleDeleteAppointment(false)
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!!error && (
            <Alert variant="danger" className="mb-0 pr-5 pl-5">
              <strong>Error:</strong> <pre className="mb-0">{error}</pre>
            </Alert>
          )}
          {!isLoading ? (
            <div className="container">
              <form className="new-appointment-form mt-5 mb-5" ref={formRef} onSubmit={handleClickSave}>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center">
                    {canScanOrUpdate && (
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm scan-document-btn"
                        onClick={() => setShowCameraScanModal(true)}
                        title="Scan clinical document for this appointment"
                      >
                        <FAIcon name="camera" className="mr-1" /> Scan Document
                      </button>
                    )}
                    {canScanOrUpdate && (
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm dictate-notes-btn ml-2"
                        onClick={() => setShowSpeechModal(true)}
                        title="Dictate clinical note and activities for this appointment"
                      >
                        <FAIcon name="microphone" className="mr-1" /> Dictate Note & Time
                      </button>
                    )}
                  </div>
                  {!!appointmentNumber && (
                    <div className="d-flex align-items-center">
                      {canScanOrUpdate && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Clone this appointment into a new scheduled visit</Tooltip>}
                        >
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm mr-2 duplicate-appointment-btn"
                            onClick={handleDuplicateAppointment}
                          >
                            <FAIcon name="copy" className="mr-1" /> Duplicate Appointment
                          </button>
                        </OverlayTrigger>
                      )}
                      <OverlayTrigger placement="left" overlay={<Tooltip>Delete this appointment</Tooltip>}>
                        <button
                          type="button"
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => setShowDeleteConfirm(true)}
                        >
                          <FAIcon name="trash-alt" type="far" className="mr-1" /> Delete Appointment
                        </button>
                      </OverlayTrigger>
                    </div>
                  )}
                </div>
                {duplicatedFromInfo && !appointmentNumber && (
                  <Alert
                    variant="info"
                    className="mb-3 py-2 px-3 d-flex justify-content-between align-items-center flex-wrap"
                  >
                    <div className="d-flex align-items-center flex-wrap">
                      <FAIcon name="copy" className="mr-2 text-primary" />
                      <span>
                        <strong>Duplicated from {duplicatedFromInfo.sourceTitle}</strong> — provider, location, and
                        service settings have been copied. Select a target Date and Time below.
                      </span>
                      {duplicatedFromInfo.followUpSuggestion && (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary ml-3 my-1 suggest-followup-date-btn"
                          onClick={() =>
                            handleApplyFollowUpSuggestion(duplicatedFromInfo.followUpSuggestion.suggestedDate)
                          }
                          title={`Detected in prior notes: "${duplicatedFromInfo.followUpSuggestion.matchedText}"`}
                        >
                          <FAIcon name="calendar-alt" className="mr-1" />
                          Suggest {duplicatedFromInfo.followUpSuggestion.displayDate} (
                          {duplicatedFromInfo.followUpSuggestion.label})
                        </button>
                      )}
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-muted p-0 ml-2"
                      onClick={() => setDuplicatedFromInfo(null)}
                      title="Dismiss notice"
                    >
                      <FAIcon name="times" />
                    </button>
                  </Alert>
                )}
                <div className="form-row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label className="form-label mt-2" htmlFor={'title'}>
                        Event Title
                      </label>
                      <input
                        type="text"
                        {...createInputProps({ name: 'title', model: appointment, setModelFunction: setAppointment })}
                        className="form-control"
                        maxLength={SUGGESTED_FIELD_LENGTH}
                      />
                      <ErrorMessage name="title" model={appointment} />
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label mt-2" htmlFor={'client'}>
                        Client
                      </label>
                      <CustomDropDown
                        id="caseNumber"
                        name="caseNumber"
                        value={caseNumber || getValue('caseNumber', null, appointment)}
                        isDisabled={!!caseNumber}
                        className="drop-down-container"
                        items={clientOptions}
                        placeholderText="Search Clients"
                        noOptionsMessage="No results found"
                        onChange={handleClientSelectChange}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage name="caseNumber" model={appointment} />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label mt-2" htmlFor={'provider'}>
                        Provider Organization
                      </label>
                      <CustomDropDown
                        className="drop-down-container"
                        items={providerOptions}
                        placeholderText="Search Providers"
                        noOptionsMessage="No results found"
                        allowFreeType="true"
                        isClearable={true}
                        {...createInputProps({
                          name: 'provider',
                          model: appointment,
                          setModelFunction: setAppointment,
                          defaultValue: null,
                        })}
                        onChange={handleProviderChange}
                      />
                      <ErrorMessage name="provider" model={appointment} />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label mt-2" htmlFor={'individualProvider'}>
                        Individual Provider
                      </label>
                      <CustomDropDown
                        className="drop-down-container"
                        items={individualProviderOptions}
                        placeholderText="Search Individual..."
                        noOptionsMessage="No results found"
                        allowFreeType="true"
                        isClearable={true}
                        {...createInputProps({
                          name: 'individualProvider',
                          model: appointment,
                          setModelFunction: setAppointment,
                          defaultValue: null,
                        })}
                        onChange={handleIndividualProviderChange}
                      />
                      <ErrorMessage name="individualProvider" model={appointment} />
                    </div>
                  </div>
                </div>

                {/* Provider Address override — shown when provider has multiple addresses */}
                {providerAddressOptions.length > 1 && (
                  <div className="form-row">
                    <div className="col-md-8">
                      <div className="form-group">
                        <label className="form-label mt-2">Provider Address</label>
                        <CustomDropDown
                          className="drop-down-container"
                          items={providerAddressOptions}
                          placeholderText="Change provider address..."
                          onChange={handleProviderAddressSelect}
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-row">
                  <div className="col-md-4">
                    <div className="multi-field-group">
                      <label className="form-label mt-2" htmlFor={'date'}>
                        Date
                      </label>
                      <DatePicker
                        {...createInputProps({
                          name: 'date',
                          model: appointmentDateAndTime,
                          setModelFunction: setAppointmentDateAndTime,
                        })}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="multi-field-group  full-width-time-picker">
                      <label className="form-label mt-2" htmlFor={'startTime'}>
                        Start Time
                      </label>
                      <TimePicker
                        {...createInputProps({
                          name: 'startTime',
                          model: appointmentDateAndTime,
                          setModelFunction: setAppointmentDateAndTime,
                        })}
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="full-width-time-picker">
                      <label className="form-label mt-2" htmlFor={'endTime'}>
                        End Time
                      </label>
                      <TimePicker
                        {...createInputProps({
                          name: 'endTime',
                          model: appointmentDateAndTime,
                          setModelFunction: setAppointmentDateAndTime,
                        })}
                        openToDate={
                          appointmentDateAndTime.startTime
                            ? Moment(appointmentDateAndTime.startTime).toDate()
                            : undefined
                        }
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <label className="form-label mt-2" htmlFor={'assignedToUserId'}>
                        Assigned To
                      </label>
                      <CustomDropDown
                        id="assignedToUserId"
                        name="assignedToUserId"
                        value={getValue('assignedToUserId', '', appointment)}
                        className="drop-down-container"
                        items={caseMemberOptions}
                        placeholderText="Unassigned"
                        noOptionsMessage="No members found"
                        onChange={e => setAppointment(prev => ({ ...prev, assignedToUserId: e.target.value }))}
                        onBlur={handleBlur}
                      />
                      <ErrorMessage name="assignedToUserId" model={appointment} />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  {appointmentDateTimeErrors.requiredErrorMessage && (
                    <div className="form-row pl-1">
                      <div className="text-danger form-text">{appointmentDateTimeErrors.requiredErrorMessage}</div>
                    </div>
                  )}
                  {appointmentDateTimeErrors.errorMessage && (
                    <div className="form-row pl-1">
                      <div className="text-danger form-text">{appointmentDateTimeErrors.errorMessage}</div>
                    </div>
                  )}
                </div>
                <div className="form-row">
                  <div className="col-md-12">
                    <div className="form-group">
                      <label className="form-label mt-2">Address</label>
                      <SmartAddressInput
                        value={{
                          street: appointment.address?.line1 ?? '',
                          city: appointment.address?.city ?? '',
                          state: appointment.address?.state ?? '',
                          zip: appointment.address?.zip ?? '',
                        }}
                        onChange={newAddr => {
                          setModelValues(
                            [
                              { name: 'address.line1', value: newAddr.street },
                              { name: 'address.city', value: newAddr.city },
                              { name: 'address.state', value: newAddr.state },
                              { name: 'address.zip', value: newAddr.zip },
                            ],
                            appointment,
                            setAppointment,
                          )
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="col-md-8">
                    <div className="form-group">
                      <label className="form-label mt-2" htmlFor={'description'}>
                        Description
                      </label>
                      <input
                        type="text"
                        {...createInputProps({
                          name: 'description',
                          model: appointment,
                          setModelFunction: setAppointment,
                        })}
                        className="form-control"
                        maxLength={SUGGESTED_FIELD_LENGTH}
                      />
                    </div>
                  </div>
                </div>
                <div className="form-row mt-2">
                  <div className="col-md-4">
                    <div className="form-group">
                      <CheckBox
                        {...createInputProps({
                          name: 'transportationServicesNeeded',
                          model: appointment,
                          setModelFunction: setAppointment,
                        })}
                      >
                        Transportation Services
                      </CheckBox>
                    </div>
                    {!!getValue('transportationServicesNeeded', '', appointment) && (
                      <TransportationServiceSelecter
                        name="transportationServiceId"
                        value={getValue('transportationServiceId', null, appointment)}
                        services={transportationServices}
                        onChange={e =>
                          setModelValues(
                            [{ name: 'transportationServiceId', value: e ? e.target.value : null }],
                            appointment,
                            setAppointment,
                          )
                        }
                      />
                    )}
                  </div>
                  <div className="col-md-4">
                    <div className="form-group">
                      <CheckBox
                        {...createInputProps({
                          name: 'translationServicesNeeded',
                          model: appointment,
                          setModelFunction: setAppointment,
                        })}
                      >
                        Translation Services
                      </CheckBox>
                    </div>
                    {!!getValue('translationServicesNeeded', '', appointment) && (
                      <TranslationServiceSelecter
                        name="translationServiceId"
                        value={getValue('translationServiceId', null, appointment)}
                        services={translationServices}
                        onChange={e =>
                          setModelValues(
                            [{ name: 'translationServiceId', value: e ? e.target.value : null }],
                            appointment,
                            setAppointment,
                          )
                        }
                      />
                    )}
                  </div>
                </div>

                {/* Embedded Activities, Notes, Tasks, and Expenses */}
                {!!appointmentNumber && (
                  <React.Fragment>
                    <hr className="mt-5" />
                    <div className="d-flex flex-wrap align-items-center justify-content-between mt-5 mb-3">
                      <h6 className="mb-1 mr-3">Time</h6>
                      <div
                        className="d-flex flex-wrap align-items-center"
                        style={{ gap: '6px' }}
                        data-testid="appointment-quick-time-pills"
                      >
                        <span className="text-muted small mr-1">
                          <i className="fas fa-stopwatch text-primary mr-1" />
                          Quick Log:
                        </span>
                        {[
                          {
                            id: 'post-visit-charting',
                            label: '+ Post-Visit Charting (0.3h)',
                            type: 'Record Review',
                            time: 0.3,
                            getDescription: prov =>
                              `Post-appointment clinical synthesis, treatment plan updates, and charting (${prov}).`,
                          },
                          {
                            id: 'work-status-review',
                            label: '+ Work Status Review (0.2h)',
                            type: 'Record Review',
                            time: 0.2,
                            getDescription: prov =>
                              `Reviewed updated work status restrictions and functional capacity documentation (${prov}).`,
                          },
                          {
                            id: 'adjuster-tcon',
                            label: '+ Adjuster Update / T-Con (0.2h)',
                            type: 'T-Con',
                            time: 0.2,
                            getDescription: prov =>
                              `Telephonic update to claims adjuster regarding appointment outcome and next steps (${prov}).`,
                          },
                          {
                            id: 'provider-coord',
                            label: '+ Provider Coordination (0.2h)',
                            type: 'T-Con',
                            time: 0.2,
                            getDescription: prov =>
                              `Care coordination with ${prov} office regarding follow-up orders and scheduling.`,
                          },
                        ].map(preset => (
                          <button
                            key={preset.id}
                            type="button"
                            data-testid={`appt-quick-time-${preset.id}`}
                            className="btn btn-sm btn-outline-primary py-0 px-2"
                            style={{ fontSize: '0.75rem', borderRadius: '12px' }}
                            onClick={() => handleQuickAddActivityPreset(preset)}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    {getValue('activities', [], appointment).length > 0 && (
                      <div className="form-row hide-sm-down" style={{ paddingLeft: '11px', paddingRight: '11px' }}>
                        <p className="form-label col-md-2 mb-1">Date</p>
                        <p className="form-label col-md-2 mb-1">Type of Activity</p>
                        <p className="form-label col-md-1 mb-1">Time</p>
                        <p className="form-label col-md-4 mb-1">Description</p>
                        <p className="form-label col-md-2 mb-1">Case Member</p>
                        <p className="form-label col-md-1 mb-1"></p>
                      </div>
                    )}
                    <div className="position-relative">
                      {(() => {
                        const activities = getValue('activities', [], appointment)
                        const allExpenses = getValue('expenses', [], appointment)
                        return activities.map((x, index) => {
                          const namePrefix = `activities[${index}]`
                          const showMoveUp =
                            index > 0 &&
                            (x.date ? new Date(x.date).toDateString() : '') ===
                              (activities[index - 1].date ? new Date(activities[index - 1].date).toDateString() : '')
                          const showMoveDown =
                            index < activities.length - 1 &&
                            (x.date ? new Date(x.date).toDateString() : '') ===
                              (activities[index + 1].date ? new Date(activities[index + 1].date).toDateString() : '')

                          const nestedExpenses = allExpenses
                            .map((exp, expIdx) => ({ exp, expIdx }))
                            .filter(({ exp }) => exp.clientActivityId === x.clientActivityId)

                          return (
                            <Activity
                              namePrefix={namePrefix}
                              key={index}
                              index={index}
                              model={appointment}
                              setModelFunction={setAppointment}
                              createInputProps={createInputProps}
                              ErrorMessage={ErrorMessage}
                              handleRemove={() => handleRemoveActivity(index)}
                              onMoveUp={showMoveUp ? () => handleReorder(index, index - 1) : null}
                              onMoveDown={showMoveDown ? () => handleReorder(index, index + 1) : null}
                              onDragStart={e => handleDragStart(e, index)}
                              onDragOver={e => handleDragOver(e, index)}
                              onDrop={e => handleDrop(e, index)}
                              onDragEnd={() => setDraggedIndex(null)}
                              caseMemberOptions={caseMemberOptions}
                            >
                              <div
                                className="pl-4 mt-2 mb-3 border-left"
                                style={{
                                  background: '#f6f6f6',
                                  padding: '10px 10px 10px 20px',
                                  borderRadius: '4px',
                                }}
                              >
                                {nestedExpenses.length > 0 && (
                                  <div className="form-row hide-sm-down">
                                    <p className="form-label col-md-2 mb-1">Date</p>
                                    <p className="form-label col-md-2 mb-1">Type of Expense</p>
                                    <p className="form-label col-md-1 mb-1">Amount</p>
                                    <p className="form-label col-md-1 mb-1">Distance</p>
                                    <p className="form-label col-md-3 mb-1">Description</p>
                                    <p className="form-label col-md-2 mb-1">Case Member</p>
                                    <p className="form-label col-md-1 mb-1"></p>
                                  </div>
                                )}
                                {nestedExpenses.map(({ exp, expIdx }) => {
                                  const expPrefix = `expenses[${expIdx}]`
                                  const expenseIsMileage = exp.type === 'Mileage'
                                  return (
                                    <Expense
                                      namePrefix={expPrefix}
                                      key={expIdx}
                                      createInputProps={createInputProps}
                                      onChangeHandler={expenseOnChangeHandler}
                                      model={appointment}
                                      setModelFunction={setAppointment}
                                      ErrorMessage={ErrorMessage}
                                      getValue={getValue}
                                      expenseIsMileage={expenseIsMileage}
                                      handleRemove={() =>
                                        removeNestedItemFromArray('expenses', expIdx, appointment, setAppointment)
                                      }
                                      caseMemberOptions={caseMemberOptions}
                                    />
                                  )
                                })}
                                <AddButton
                                  id={`addExpenseButton-${index}`}
                                  className={nestedExpenses.length > 0 ? 'inline-add-button mt-2' : 'mt-2'}
                                  onClick={() => handleAddExpense(x.clientActivityId)}
                                >
                                  + Expense
                                </AddButton>
                              </div>
                            </Activity>
                          )
                        })
                      })()}
                      <AddButton
                        id="addActivityButton"
                        className={getValue('activities', [], appointment).length > 0 ? 'inline-add-button' : ''}
                        onClick={handleAddActivity}
                      >
                        Add Activity
                      </AddButton>
                      {!!showRequiredActivitiesError && (
                        <div className={'text-danger form-text'}>At least one activity must be added</div>
                      )}
                      {getValue('activities', [], appointment).length > 0 && !isCompletable && (
                        <div className={'text-warning form-text mt-2'}>
                          {isUpcoming
                            ? 'Note: Upcoming appointments cannot be completed until their date has passed.'
                            : 'Note: All activities must have a time greater than 0 to complete this appointment.'}
                        </div>
                      )}
                    </div>

                    <hr />
                    <h6 className="mt-5">Appointment Notes</h6>
                    <div className="mb-4">
                      {(getValue('notes', [], appointment) || []).map((note, index) => (
                        <div key={index} className="card p-3 mb-2 bg-light">
                          {editingNoteIndex === index ? (
                            <div className="form-group mb-0">
                              <TextArea
                                areaProps={{
                                  value: editingNoteText,
                                  onChange: value => setEditingNoteText(value),
                                  placeholder: 'Edit note content...',
                                  className: 'form-control',
                                }}
                                rows="3"
                              />
                              <div className="mt-2">
                                <button
                                  type="button"
                                  className="btn btn-primary btn-sm mr-2"
                                  onClick={() => handleSaveEditNote(index)}
                                  disabled={!editingNoteText.trim()}
                                >
                                  Save Note
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={handleCancelEditNote}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <React.Fragment>
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <small className="text-muted">
                                  {format.dateTime(note.createdUtc)} by {note.createdBy}
                                </small>
                                <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-primary"
                                    onClick={() => handleStartEditNote(index, note.note)}
                                    title="Edit note"
                                  >
                                    <FAIcon name="pen" type="fas" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0 text-danger"
                                    onClick={() => handleRemoveNote(index)}
                                    title="Delete note"
                                  >
                                    <FAIcon name="trash-alt" type="far" />
                                  </button>
                                </div>
                              </div>
                              <div
                                className="mb-0"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.note) }}
                              />
                            </React.Fragment>
                          )}
                        </div>
                      ))}

                      <div className="form-group mt-3">
                        <label className="form-label">Add New Note</label>
                        <TextArea
                          areaProps={{
                            value: newNote,
                            onChange: value => setNewNote(value),
                            placeholder: 'Enter note content...',
                            className: 'form-control',
                          }}
                          rows="3"
                        />
                        <button
                          type="button"
                          className="btn btn-secondary mt-2"
                          onClick={handleAddNote}
                          disabled={!newNote.trim()}
                        >
                          Add Note
                        </button>
                      </div>
                    </div>

                    <hr className="mt-5" />
                    <div className="form-row form-group">
                      <div className="col-md-3">
                        <h6 className="mt-2">Next Steps</h6>
                      </div>
                      <div className="col-md-9">
                        {getValue('tasks', [], appointment).map((task, index) => (
                          <div key={index}>
                            <div className="form-row mx-0 mt-1 align-items-center">
                              <div className="col-7 pl-0">
                                <input
                                  {...createInputProps({
                                    name: `tasks[${index}].title`,
                                    model: appointment,
                                    setModelFunction: setAppointment,
                                  })}
                                  type="text"
                                  className="form-control"
                                  placeholder="Title"
                                />
                                <ErrorMessage name={`tasks[${index}].title`} model={appointment} />
                              </div>
                              <div className="col-4">
                                <DatePicker
                                  {...createInputProps({
                                    name: `tasks[${index}].dueDateUtc`,
                                    model: appointment,
                                    setModelFunction: setAppointment,
                                  })}
                                  placeholderText="Due Date"
                                />
                                <ErrorMessage name={`tasks[${index}].dueDateUtc`} model={appointment} />
                              </div>
                              <div className="col-1 px-0 text-center">
                                <RemoveButton
                                  id={`removeTask[${index}]`}
                                  onClick={() => removeNestedItemFromArray('tasks', index, appointment, setAppointment)}
                                />
                              </div>
                            </div>
                          </div>
                        ))}
                        <div className="form-row mt-1">
                          <AddButton id="addTask" onClick={handleAddTask}>
                            Add Task
                          </AddButton>
                        </div>
                      </div>
                    </div>

                    {(() => {
                      const looseExpenses = getValue('expenses', [], appointment)
                        .map((exp, expIdx) => ({ exp, expIdx }))
                        .filter(({ exp }) => !exp.clientActivityId)

                      return (
                        <React.Fragment>
                          <hr className="mt-5" />
                          <h6 className="mt-5 mb-4">Additional Expenses</h6>
                          {looseExpenses.length > 0 && (
                            <div className="form-row hide-sm-down">
                              <p className="form-label col-md-2 mb-1">Date</p>
                              <p className="form-label col-md-2 mb-1">Type of Expense</p>
                              <p className="form-label col-md-1 mb-1">Amount</p>
                              <p className="form-label col-md-1 mb-1">Distance</p>
                              <p className="form-label col-md-3 mb-1">Description</p>
                              <p className="form-label col-md-2 mb-1">Case Member</p>
                              <p className="form-label col-md-1 mb-1"></p>
                            </div>
                          )}
                          <div className="position-relative mb-5">
                            {looseExpenses.map(({ exp, expIdx }) => {
                              const expPrefix = `expenses[${expIdx}]`
                              const expenseIsMileage = exp.type === 'Mileage'
                              return (
                                <Expense
                                  namePrefix={expPrefix}
                                  key={expIdx}
                                  createInputProps={createInputProps}
                                  onChangeHandler={expenseOnChangeHandler}
                                  model={appointment}
                                  setModelFunction={setAppointment}
                                  ErrorMessage={ErrorMessage}
                                  getValue={getValue}
                                  expenseIsMileage={expenseIsMileage}
                                  handleRemove={() =>
                                    removeNestedItemFromArray('expenses', expIdx, appointment, setAppointment)
                                  }
                                  caseMemberOptions={caseMemberOptions}
                                />
                              )
                            })}
                            <AddButton
                              id="addExpenseButton"
                              className={looseExpenses.length > 0 ? 'inline-add-button' : ''}
                              onClick={() => handleAddExpense(null)}
                            >
                              Add Additional Expense
                            </AddButton>
                          </div>
                        </React.Fragment>
                      )
                    })()}
                  </React.Fragment>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-end align-items-center border-top pt-4">
                  {canScanOrUpdate && (
                    <button
                      type="button"
                      className="btn btn-outline-success mr-2 scan-document-btn"
                      onClick={() => setShowCameraScanModal(true)}
                      title="Scan clinical document for this appointment"
                    >
                      <FAIcon name="camera" className="mr-1" /> Scan Document
                    </button>
                  )}
                  {canScanOrUpdate && (
                    <button
                      type="button"
                      className="btn btn-outline-primary mr-auto dictate-notes-btn"
                      onClick={() => setShowSpeechModal(true)}
                      title="Dictate clinical note and activities for this appointment"
                    >
                      <FAIcon name="microphone" className="mr-1" /> Dictate Note & Time
                    </button>
                  )}
                  <button type="button" className="btn btn-secondary mr-2" onClick={handleClickExit}>
                    Cancel
                  </button>
                  {!!appointmentNumber && !appointment.complete && (
                    <button
                      type="button"
                      className="btn btn-success mr-2"
                      onClick={handleCompleteAppointment}
                      disabled={!isCompletable}
                    >
                      Complete
                    </button>
                  )}
                  <button type="button" className="btn btn-primary" onClick={handleClickSave}>
                    {appointment.complete ? 'Save' : 'Save Draft'}
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div className="d-flex justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}
          {showTranslationModal && (
            <NewCompanyModal
              title="Translation Service"
              onSave={handleTranslationSave}
              onSaveSuccess={handleTranslationSaveSuccess}
              onClosed={() => setShowTranslationModal(false)}
              oldModel={getServiceOldModel(serviceToEdit, translationServices)}
            />
          )}
          {showTransportationModal && (
            <NewCompanyModal
              title="Transportation Service"
              onSave={handleTransportationSave}
              onSaveSuccess={handleTransportationSaveSuccess}
              onClosed={() => setShowTransportationModal(false)}
              oldModel={getServiceOldModel(serviceToEdit, transportationServices)}
            />
          )}
          {showCameraScanModal && (
            <CameraScanModal
              show={showCameraScanModal}
              onHide={() => setShowCameraScanModal(false)}
              appointmentId={
                appointmentNumber
                  ? parseInt(appointmentNumber, 10)
                  : appointment?.appointmentNumber
                  ? parseInt(appointment.appointmentNumber, 10)
                  : appointment?.id || null
              }
              associatedProviderName={appointment.individualProvider || appointment.provider || ''}
              associatedProviderId={selectedProvider?.id || null}
              providers={treatmentProviders || []}
              onUpload={handleCameraUploadFromAppointment}
            />
          )}
          {showHitlModal && (
            <HitlReviewModal
              show={showHitlModal}
              onHide={() => setShowHitlModal(false)}
              title="Smart Upload - Appointment Document"
              isAnalyzing={isAnalyzingSmartUpload}
              documentData={{
                caseNumber: caseNumber || appointment.caseNumber,
                documents: smartUploadDocuments,
                fileName: smartUploadDocuments[0]?.fileName || 'Scanned_Appointment_Document.jpg',
                fileType: smartUploadDocuments[0]?.fileType || 'image',
                textContent: smartUploadDocuments[0]?.textContent || '',
                url: smartUploadDocuments[0]?.url || null,
              }}
              rawExtractedData={isAnalyzingSmartUpload ? null : hitlCaseModel?.pendingExtractions || hitlCaseModel}
              existingCase={hitlCaseModel || {}}
              onApplyChanges={() => {
                setShowHitlModal(false)
                cogoToast.success('Extractions applied to case successfully!', { position: 'top-right' })
                if (refreshCaseStatus) refreshCaseStatus()
              }}
              user={currentUser}
            />
          )}
          {showSpeechModal && (
            <SpeechReviewModal
              show={showSpeechModal}
              onHide={() => setShowSpeechModal(false)}
              caseNumber={caseNumber || appointment?.caseNumber}
              appointmentId={
                appointmentNumber
                  ? parseInt(appointmentNumber, 10)
                  : appointment?.appointmentNumber
                  ? parseInt(appointment.appointmentNumber, 10)
                  : appointment?.id || null
              }
              user={currentUser}
              onApply={handleApplySpeechEntities}
            />
          )}
        </div>
      </Slide>
    </React.Fragment>
  )
})

export default NewAppointment
